import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { formatMonthKeyLabel, shiftMonthKey } from '../../hooks/useSpendBreakdown';

type Props = {
  monthKey: string;
  onChange: (nextKey: string) => void;
  /** Newest month the user can select (usually current calendar month). */
  maxMonthKey?: string;
};

function currentMonthKey(): string {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** Compact ← Month YYYY → navigator for Spend. */
export function SpendMonthNavigator({ monthKey, onChange, maxMonthKey }: Props) {
  const maxKey = maxMonthKey ?? currentMonthKey();
  const atMax = monthKey >= maxKey;
  const label = formatMonthKeyLabel(monthKey);
  const isCurrent = monthKey === maxKey;

  return (
    <View style={styles.wrap}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Previous month"
        hitSlop={10}
        onPress={() => onChange(shiftMonthKey(monthKey, -1))}
        style={({ pressed }) => [styles.chevron, pressed && styles.pressed]}
      >
        <Text style={styles.chevronText}>‹</Text>
      </Pressable>

      <View style={styles.center}>
        <Text style={styles.label}>{label}</Text>
        {!isCurrent ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Jump to this month"
            hitSlop={6}
            onPress={() => onChange(maxKey)}
            style={({ pressed }) => [styles.jump, pressed && styles.pressed]}
          >
            <Text style={styles.jumpText}>This month</Text>
          </Pressable>
        ) : (
          <Text style={styles.hint}>This month</Text>
        )}
      </View>

      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Next month"
        hitSlop={10}
        disabled={atMax}
        onPress={() => {
          if (atMax) return;
          const next = shiftMonthKey(monthKey, 1);
          onChange(next > maxKey ? maxKey : next);
        }}
        style={({ pressed }) => [
          styles.chevron,
          atMax && styles.chevronDisabled,
          pressed && !atMax && styles.pressed,
        ]}
      >
        <Text style={[styles.chevronText, atMax && styles.chevronTextDisabled]}>›</Text>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.xs,
    borderRadius: radii.lg,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.glassBorder,
    backgroundColor: 'rgba(255,255,255,0.03)',
  },
  chevron: {
    width: 40,
    height: 40,
    borderRadius: radii.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chevronDisabled: {
    opacity: 0.35,
  },
  chevronText: {
    color: colors.text,
    fontSize: 28,
    lineHeight: 32,
    fontWeight: '300',
  },
  chevronTextDisabled: {
    color: colors.textMuted,
  },
  center: {
    flex: 1,
    alignItems: 'center',
    gap: 2,
  },
  label: {
    ...typography.headline,
    color: colors.text,
    fontSize: 17,
  },
  hint: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 11,
  },
  jump: {
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  jumpText: {
    ...typography.caption,
    color: colors.accent,
    fontWeight: '700',
    fontSize: 11,
  },
  pressed: {
    opacity: 0.75,
  },
});
